/**
 * `skaile preset export` — pure core: turn a project's own `skaile.yaml`
 * (`SkWorkspaceConfig`) into a shareable `PresetManifest`, with first-class
 * detection and redaction of inline secrets.
 *
 * These helpers are intentionally free of I/O and prompting so they can be
 * unit-tested in isolation; the Commander action in `preset-cmd.ts` owns file
 * reads/writes, the loud stderr summary, and interactive confirmation.
 */

import { existsSync } from "node:fs";
import * as path from "node:path";
import { FACTORY_SOURCE_URL, normalizeRepoUrl } from "@skaile/workspaces/asset-manager";
import {
  type LockFile,
  type SkWorkspaceConfig,
  type SourceEntry,
  storeManifestSlug,
} from "@skaile/workspaces/core";
import { getStoreManifestsDir } from "@skaile/workspaces/library";
import type { Placeholder, PresetItem, PresetManifest } from "@skaile/workspaces/types/manifests";

/** One inline-secret hit: which item, where in its config, and a safe preview. */
export interface SecretFinding {
  /** `id` of the offending preset item (connector/mcp id). */
  itemId: string;
  /** Dotted path into the item's `config`, e.g. `options.dsn` or `env.TOKEN`. */
  path: string;
  /** Value with its secret portion masked, safe to print to a terminal. */
  maskedValue: string;
}

// ── Preset construction ───────────────────────────────────────────────────────

/**
 * Build a self-consistent {@link PresetManifest} from a workspace config.
 *
 * Mapping (a future `preset apply` reverses this):
 * - `dependencies[]` → `{ ref }` items, ref copied VERBATIM (no re-pinning).
 * - `connectors[]` → `{ id, ref: "connector:<driver>", config }` items.
 * - `mcp_servers[]` → `{ id, ref: "mcp:<id>", config }` items.
 *
 * `agent_config` is deliberately NOT exported — it is personal/machine-specific
 * (driver, model, API-cloud transport) and does not belong in a shared preset.
 *
 * @param config - The project-own `skaile.yaml` (NOT the merged/global config).
 * @param name - Preset name; also the default output filename stem.
 * @param sources - Git sources the dep items resolve from (see {@link neededSources}).
 * @returns A manifest that passes `validatePreset` whenever it has ≥1 item.
 */
export function buildPresetFromWorkspace(
  config: SkWorkspaceConfig,
  name: string,
  sources: SourceEntry[] = [],
): PresetManifest {
  const items: PresetItem[] = [];

  for (const ref of config.dependencies ?? []) {
    items.push({ ref });
  }

  for (const connector of config.connectors ?? []) {
    const config_ = compact({
      access: connector.access,
      auth: connector.auth,
      options: connector.options,
      version: connector.version,
      tags: connector.tags,
    });
    items.push({
      id: connector.id,
      ref: `connector:${connector.driver}`,
      ...(config_ ? { config: config_ } : {}),
    });
  }

  for (const mcp of config.mcp_servers ?? []) {
    // `auth`/`recipe`/`mount` are intentionally NOT exported: a raw mcp `auth`
    // token would be a leak vector, and the rest are machine-specific. Round-trip
    // fidelity is deliberately traded for share-safety here.
    const config_ = compact({
      transport: mcp.transport,
      command: mcp.command,
      args: mcp.args,
      env: mcp.env,
      url: mcp.url,
      headers: mcp.headers,
    });
    items.push({
      id: mcp.id,
      ref: `mcp:${mcp.id}`,
      ...(config_ ? { config: config_ } : {}),
    });
  }

  const project = config.name ?? name;
  return {
    name,
    version: "1.0.0",
    description: `Exported from ${project} workspace`,
    items,
    // Never let a credential-bearing source URL reach a shareable preset.
    ...(sources.length > 0
      ? { sources: sources.map((s) => ({ ...s, url: stripUrlCredentials(s.url) })) }
      : {}),
  };
}

// ── Source provenance ─────────────────────────────────────────────────────────

/**
 * The subset of `config.sources` a cross-repo apply must register before install.
 *
 * The lock's top-level `sources` list is the full **transitive** closure of every
 * source that contributed a resolved asset, so filtering `config.sources` to it
 * (minus the always-available factory source) captures direct + transitive deps
 * with no drop risk. With no lock (provenance unknown), every non-factory source
 * is kept so apply still resolves. Connectors/mcp carry no source.
 */
export function neededSources(config: SkWorkspaceConfig, lock: LockFile | null): SourceEntry[] {
  const factoryUrl = normalizeRepoUrl(FACTORY_SOURCE_URL);
  const nonFactory = (config.sources ?? []).filter((s) => normalizeRepoUrl(s.url) !== factoryUrl);
  if (!lock) return nonFactory;
  const inClosure = new Set(lock.sources.map((s) => normalizeRepoUrl(s.url)));
  return nonFactory.filter((s) => inClosure.has(normalizeRepoUrl(s.url)));
}

/**
 * Of the captured `neededSources`, the ones whose assets resolve through a
 * MACHINE-LOCAL curated store manifest (`~/.skaile/store/manifests/<slug>.yaml`,
 * written by `source manifest init`). Those overlays are not shared with the
 * preset, so a recipient on another machine won't reproduce the same resolution
 * until the sidecar is itself published. Returns the affected source URLs (empty
 * when none carry a sidecar).
 */
export function sourcesWithSidecar(sources: SourceEntry[]): string[] {
  const dir = getStoreManifestsDir();
  return sources
    .filter((s) => existsSync(path.join(dir, `${storeManifestSlug(s.url)}.yaml`)))
    .map((s) => s.url);
}

/** Drop `undefined`/`null` fields; return `undefined` when nothing remains. */
function compact(obj: Record<string, unknown>): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    // Null too — YAML parses a blank field (`auth: ~`) to null, which must not
    // survive into the exported preset as `auth: null`.
    if (v !== undefined && v !== null) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// ── Secret detection ──────────────────────────────────────────────────────────

/** SSOT prefix list for values that are already safe indirection references. */
const SECRET_REFERENCE_PREFIXES = ["env", "forge", "secret", "op", "kp", "oauth", "pat"] as const;
const SECRET_REFERENCE_RE = new RegExp(`^(${SECRET_REFERENCE_PREFIXES.join("|")}):`);

/**
 * True when `value` is a secret *reference* (indirection) rather than an inline
 * secret — e.g. `env:DATABASE_URL`, `forge:api-key`, `pat:env:GH_TOKEN`. Such
 * values are safe to publish and are never flagged.
 */
export function isSecretReference(value: string): boolean {
  return SECRET_REFERENCE_RE.test(value);
}

/** A `${INPUT:KEY}` / `${SECRET:KEY}` interpolation is a resolved placeholder. */
const PLACEHOLDER_RE = /^\$\{(INPUT|SECRET):[^}]+\}$/;

/**
 * Key names that mark a credential-bearing field. Matched component-wise with
 * `_`/`-` as separators so env-style names hit (`GH_TOKEN`, `API_KEY`) while
 * innocent substrings do NOT (`monkey`, `keyboard`, `Content-Type`). `url`/`conn`
 * are deliberately absent — a bare URL/host is not a secret; credential-bearing
 * URLs are caught by {@link PROTO_CREDENTIAL_URL_RE}. Over-flagging trains users
 * to ignore the warning, so precision here matters as much as recall.
 */
const SUSPICIOUS_KEY_RE =
  /(?:^|[_-])(?:token|password|passwd|secret|apikey|key|dsn|credential|auth|authorization|bearer)(?:[_-]|$)/i;
/** A `proto://user:pass@host` URL literally embeds a password. */
const PROTO_CREDENTIAL_URL_RE = /\w+:\/\/[^/@\s]+:[^/@\s]+@/;

/** True when a source URL embeds `user:pass@` credentials (private-HTTPS auth form). */
export function urlHasCredentials(url: string): boolean {
  return PROTO_CREDENTIAL_URL_RE.test(url);
}

/**
 * Strip `user:pass@` userinfo from a URL. The repo is fully identified without it
 * (git auth on apply uses the target's own credential helper / ssh key), so an
 * exported source URL must never carry the token into a shared preset.
 */
export function stripUrlCredentials(url: string): string {
  return url.replace(/^(\w+:\/\/)[^/@]*:[^/@]*@/, "$1");
}

/**
 * ≥20 chars mixing ≥2 character classes — looks like a raw token. Plain URLs and
 * filesystem paths are excluded: they are long but not secret (a credential URL
 * is caught by {@link PROTO_CREDENTIAL_URL_RE} instead).
 *
 * Known gap: single-character-class blobs (all-lowercase hex, bcrypt fragments)
 * are not detected — the 2-class threshold trades recall for fewer false positives.
 */
function isLongEntropic(value: string): boolean {
  if (value.length < 20) return false;
  if (/^\w+:\/\//.test(value) || value.startsWith("/")) return false;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/].filter((re) => re.test(value)).length;
  return classes >= 2;
}

/**
 * Scan every string inside each item's `config` and return inline secrets.
 *
 * A string is flagged when it is NOT a {@link isSecretReference} AND sits in a
 * credential context: the inherent `auth` field, a suspicious key name, a
 * `proto://user:pass@` URL, or a long high-entropy blob. Items without a `config`
 * (bare deps) are skipped.
 *
 * Detection is **heuristic / best-effort**, not exhaustive: a short, low-entropy
 * secret under an innocuous key name can slip through. A clean scan is not a
 * guarantee — treat it as a safety net, not proof the file holds no secrets.
 */
export function scanInlineSecrets(preset: PresetManifest): SecretFinding[] {
  const findings: SecretFinding[] = [];
  for (const item of preset.items) {
    // Only connector/mcp items carry a `config` (and always an `id`); bare deps
    // have neither, so the fallback is just for type-narrowing.
    const itemId = typeof item.id === "string" ? item.id : "";
    if (item.config) scanNode(item.config, [], false, itemId, findings);
  }
  return findings;
}

function scanNode(
  node: unknown,
  segments: string[],
  inCredentialField: boolean,
  itemId: string,
  findings: SecretFinding[],
): void {
  if (typeof node === "string") {
    if (isInlineSecret(node, segments.at(-1) ?? "", inCredentialField)) {
      findings.push({ itemId, path: segments.join("."), maskedValue: maskSecret(node) });
    }
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((el, i) =>
      scanNode(el, [...segments, String(i)], inCredentialField, itemId, findings),
    );
    return;
  }
  if (node && typeof node === "object") {
    for (const [k, v] of Object.entries(node)) {
      // Only `auth` is an inherent credential field. `env`/`headers` are NOT
      // blanket-marked — that flagged Content-Type / plain path env vars. Their
      // real secrets are caught per-leaf by the key heuristic (Authorization,
      // *_TOKEN, …) and the value patterns.
      const credField = inCredentialField || k === "auth";
      scanNode(v, [...segments, k], credField, itemId, findings);
    }
  }
}

/** Decide whether a single string leaf is an inline secret. */
function isInlineSecret(value: string, key: string, inCredentialField: boolean): boolean {
  if (isSecretReference(value) || PLACEHOLDER_RE.test(value)) return false;
  if (inCredentialField) return true;
  if (SUSPICIOUS_KEY_RE.test(key)) return true;
  if (PROTO_CREDENTIAL_URL_RE.test(value)) return true;
  return isLongEntropic(value);
}

/**
 * Render a terminal-safe preview. Reveals **no** secret characters — the
 * finding's `path` already identifies which value it is, and a prefix would leak
 * the token type (`sk-`, `ghp_`, `xoxb-`). Only a credential URL keeps its inert
 * shape (scheme + masked user/pass/host).
 */
function maskSecret(value: string): string {
  const proto = value.match(/^(\w+):\/\/[^/@\s]+:[^/@\s]+@/);
  if (proto) return `${proto[1]}://…:****@…`;
  return "****";
}

// ── Redaction ─────────────────────────────────────────────────────────────────

/**
 * Replace each flagged inline value with a `${INPUT:<KEY>}` placeholder and add
 * a matching `secret` placeholder entry to the owning item. Returns a NEW preset
 * (the input is not mutated) plus the generated placeholder keys.
 *
 * `<KEY>` is `<itemId>_<path>` upper-snake-cased so it is stable and readable.
 */
export function redactSecrets(
  preset: PresetManifest,
  findings: SecretFinding[],
): { preset: PresetManifest; keys: string[] } {
  const next = structuredClone(preset);
  const keys: string[] = [];
  const used = new Set<string>();

  for (const finding of findings) {
    const item = next.items.find((it) => it.id === finding.itemId);
    if (!item?.config) continue;

    // Unique key per finding — two paths must never collapse onto one placeholder
    // (that would resolve two distinct secrets to a single input at apply-time).
    const base = placeholderKey(finding.itemId, finding.path);
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base}_${n}`;

    // Only record the placeholder if the value was actually replaced — a failed
    // set leaves the secret inline, so it must stay counted as unresolved.
    if (!setAtPath(item.config, finding.path.split("."), `\${INPUT:${key}}`)) continue;
    used.add(key);

    const placeholder: Placeholder = {
      key,
      type: "secret",
      required: true,
      prompt: `Secret value for ${finding.itemId}.${finding.path}`,
    };
    item.placeholders = [...(item.placeholders ?? []), placeholder];
    keys.push(key);
  }

  return { preset: next, keys };
}

/** `db` + `options.dsn` → `DB_OPTIONS_DSN`. */
function placeholderKey(itemId: string, path: string): string {
  return `${itemId}_${path}`
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

/** Set a nested value by path segments. Returns false if the path doesn't resolve. */
function setAtPath(root: Record<string, unknown>, segments: string[], value: string): boolean {
  let node: unknown = root;
  for (let i = 0; i < segments.length - 1; i++) {
    if (!node || typeof node !== "object") return false;
    node = (node as Record<string, unknown>)[segments[i]!];
  }
  if (!node || typeof node !== "object") return false;
  (node as Record<string, unknown>)[segments.at(-1)!] = value;
  return true;
}

// ── Output header ─────────────────────────────────────────────────────────────

/** Exact wording written into the file when unresolved inline secrets remain. */
export const INLINE_SECRETS_HEADER =
  "# ⚠️ CONTAINS INLINE SECRETS — replace with placeholders before sharing or publishing";

/**
 * Header comment for a preset that still holds unresolved inline secrets, or
 * `null` when `findings` is empty (nothing left to warn about).
 */
export function secretHeaderComment(findings: SecretFinding[]): string | null {
  return findings.length > 0 ? INLINE_SECRETS_HEADER : null;
}
